import { useCallback, useEffect, useMemo } from 'react'
import { BlockRenderer } from './blocks/BlockRenderer'
import type { Block } from './blocks/types'
import type { BlockAction } from './blocks/BlockActionsMenu'
import type { DraftBody } from './api'

interface Props {
  draft: DraftBody
  selectedIds: string[]
  isAgentRunning: boolean
  isAnimating: (id: string) => boolean
  onTextChange: (id: string, text: string) => void
  onToggleSelect: (id: string) => void
  onClearSelection: () => void
  onMenuAction: (intent: string, blockId: string) => void
}

const TEXT_ACTIONS = (id: string, fire: (intent: string, id: string) => void): BlockAction[] => [
  { id: 'shorten', label: 'Shorten', quick: true, onSelect: () => fire('shorten', id) },
  { id: 'regenerate', label: 'Regenerate', quick: true, onSelect: () => fire('regenerate', id) },
  { id: 'more_formal', label: 'More formal', onSelect: () => fire('more_formal', id) },
  { id: 'rephrase', label: 'Rephrase', onSelect: () => fire('rephrase', id) },
  { id: 'delete', label: 'Delete', group: 'destructive', onSelect: () => fire('delete', id) },
]

const CHART_ACTIONS = (id: string, fire: (intent: string, id: string) => void): BlockAction[] => [
  { id: 'regenerate', label: 'Regenerate', quick: true, onSelect: () => fire('regenerate', id) },
  { id: 'swap_chart', label: 'Swap chart type', onSelect: () => fire('swap_chart', id) },
  { id: 'delete', label: 'Delete', group: 'destructive', onSelect: () => fire('delete', id) },
]

const LIST_ACTIONS = (id: string, fire: (intent: string, id: string) => void): BlockAction[] => [
  { id: 'regenerate', label: 'Regenerate', quick: true, onSelect: () => fire('regenerate', id) },
  { id: 'replace_items', label: 'Replace items', onSelect: () => fire('replace_items', id) },
  { id: 'reorder', label: 'Reorder', onSelect: () => fire('reorder', id) },
  { id: 'delete', label: 'Delete', group: 'destructive', onSelect: () => fire('delete', id) },
]

function actionsForBlock(b: Block, fire: (intent: string, id: string) => void): BlockAction[] {
  switch (b.type) {
    case 'allocationComparison':
    case 'maturityTimeline':
      return CHART_ACTIONS(b.id, fire)
    case 'bulletList':
    case 'fundList':
      return LIST_ACTIONS(b.id, fire)
    default:
      return TEXT_ACTIONS(b.id, fire)
  }
}

export function ProposalDocument({
  draft,
  selectedIds,
  isAgentRunning,
  isAnimating,
  onTextChange,
  onToggleSelect,
  onClearSelection,
  onMenuAction,
}: Props) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const fire = useCallback((intent: string, id: string) => onMenuAction(intent, id), [onMenuAction])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClearSelection()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClearSelection])

  // Clear selection on click outside the document (or chat). A click that lands
  // on a block-shell handles itself via onToggleSelect; everywhere else clears.
  useEffect(() => {
    if (selectedIds.length === 0) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target) return
      // ignore clicks inside any block, the actions menu, or the chat panel
      if (target.closest('[data-testid^="block-shell-"]')) return
      if (target.closest('[data-block-interactive]')) return
      if (target.closest('[role="menu"]') || target.closest('[role="menuitem"]')) return
      if (target.closest('aside')) return
      onClearSelection()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [selectedIds.length, onClearSelection])

  return (
    <div>
      {draft.blocks.map((block) => (
        <BlockRenderer
          key={block.id}
          block={block}
          selected={selectedSet.has(block.id)}
          frozen={isAgentRunning}
          animate={isAnimating(block.id)}
          actions={actionsForBlock(block, fire)}
          onTextChange={onTextChange}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  )
}
